import { useCallback, useState } from 'react';
import type { LimosaPdfGegevens, LimosaPdfLezing } from '@/api/administratie';
import { LimosaPdfDetails, LimosaPdfStatus, useLimosaPdf } from './useLimosaPdf';

/**
 * The file and the fields of one Limosa upload. Choosing a file starts over: the
 * fields are cleared, and a PDF is read to fill them in again.
 */
export function useLimosaFileForm(read: (file: File) => Promise<LimosaPdfLezing>) {
  const [file, setFile] = useState<File | null>(null);
  const [geldigVan, setGeldigVan] = useState('');
  const [geldigTot, setGeldigTot] = useState('');
  const [limosaNummer, setLimosaNummer] = useState('');
  const prefill = useCallback((g: LimosaPdfGegevens) => {
    if (g.periodeVan && g.periodeTot) {
      setGeldigVan(g.periodeVan);
      setGeldigTot(g.periodeTot);
    }
    if (g.declaratienummer) setLimosaNummer(g.declaratienummer);
  }, []);
  const pdf = useLimosaPdf(read, prefill);

  const choose = (chosen: File | null) => {
    setFile(chosen);
    setGeldigVan('');
    setGeldigTot('');
    setLimosaNummer('');
    pdf.choose(chosen);
  };

  return {
    file, geldigVan, setGeldigVan, geldigTot, setGeldigTot, limosaNummer, setLimosaNummer,
    pdfState: pdf.state,
    choose,
    reset: () => choose(null),
    /** "Opladen" may be pressed: a file, both dates, and no PDF still being read. */
    ready: file !== null && geldigVan !== '' && geldigTot !== '' && pdf.state.kind !== 'bezig',
  };
}

type LimosaFileForm = ReturnType<typeof useLimosaFileForm>;

/** Step 1: one large tap target for the PDF or photo, with what happens next under it. */
export function LimosaFilePicker({ form }: { form: LimosaFileForm }) {
  return (
    <div className="space-y-1">
      <label
        className="flex min-h-[88px] cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-td-blue/60 bg-td-blue/5 px-3 py-4 text-center focus-within:ring-2 focus-within:ring-td-blue"
        data-testid="limosa-file-picker"
      >
        <span className="text-sm font-semibold text-td-blue">
          {form.file ? 'Ander bestand kiezen' : 'Kies de Limosa (PDF of foto)'}
        </span>
        {form.file && <span className="max-w-full truncate text-xs text-td-navy">{form.file.name}</span>}
        <input
          type="file"
          accept="application/pdf,image/*"
          aria-label="Kies de Limosa (PDF of foto)"
          onChange={(e) => form.choose(e.target.files?.[0] ?? null)}
          className="sr-only"
        />
      </label>
      {form.file
        ? <LimosaPdfStatus state={form.pdfState} />
        : <p className="text-xs text-td-muted">Een PDF vult de gegevens vanzelf in.</p>}
    </div>
  );
}

/**
 * The dates and the number, shown once a file is chosen and any PDF is read, then
 * what the PDF said about the place of employment and the person.
 */
export function LimosaFileFields({ form, person, compact = false }: {
  form: LimosaFileForm;
  person: { id: number; name: string } | undefined;
  compact?: boolean;
}) {
  if (!form.file || form.pdfState.kind === 'bezig') return null;
  const label = compact ? 'block text-xs text-td-muted' : 'block text-sm text-td-navy';
  const field = compact
    ? 'mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm'
    : 'mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm';
  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'} data-testid="limosa-fields">
      <div className={`grid grid-cols-2 ${compact ? 'gap-2' : 'gap-3'}`}>
        <label className={label}>
          Geldig van
          <input type="date" value={form.geldigVan} onChange={(e) => form.setGeldigVan(e.target.value)} className={field} />
        </label>
        <label className={label}>
          Geldig tot
          <input type="date" value={form.geldigTot} onChange={(e) => form.setGeldigTot(e.target.value)} className={field} />
        </label>
      </div>
      <label className={label}>
        Declaratienummer (optioneel)
        <input type="text" inputMode="numeric" placeholder="24912 0699 0420 9031" maxLength={30} value={form.limosaNummer} onChange={(e) => form.setLimosaNummer(e.target.value)} className={field} />
      </label>
      <LimosaPdfDetails state={form.pdfState} person={person} />
    </div>
  );
}
